import 'reflect-metadata';
import {
  FlowInputOf,
  FlowName, FlowOutputOf,
  FlowType,
  FrontMcpAuth,
  FrontMcpLogger,
  FrontMcpServer, HookRegistryInterface,
  ProviderScope,
  ScopeEntry,
  ScopeRecord,
  SessionProvider,
  Token,
  Type,
} from '@frontmcp/sdk';
import AppRegistry from '../app/app.registry';
import ProviderRegistry from '../provider/provider.registry';
import {AuthRegistry} from '../auth/auth.registry';
import FlowRegistry from '../flows/flow.registry';
import HttpRequestFlow from './flows/http.request.flow';
import {TransportService} from '../transport/transport.registry';
import ToolRegistry from '../tool/tool.registry';
import HookRegistry from "../hooks/hook.registry";


export class Scope extends ScopeEntry {
  readonly id: string;
  private readonly globalProviders: ProviderRegistry;
  readonly logger: FrontMcpLogger;

  private readonly scopeProviders: ProviderRegistry;
  private scopeAuth: AuthRegistry;
  private scopeFlows: FlowRegistry;
  private scopeApps: AppRegistry;
  private scopeHooks: HookRegistry;
  private scopeTools: ToolRegistry;

  transportService: TransportService; // TODO: migrate transport service to transport.registry
  readonly entryPath: string;
  readonly routeBase: string = '';
  readonly orchestrated: boolean = false;

  readonly server: FrontMcpServer;

  constructor(rec: ScopeRecord, globalProviders: ProviderRegistry) {
    super(rec, rec.provide);
    this.id = rec.metadata.id;
    this.logger = globalProviders.get(FrontMcpLogger).child('FrontMcp.MultiAppScope');
    this.globalProviders = globalProviders;
    this.server = this.globalProviders.get(FrontMcpServer);
    this.entryPath = rec.metadata.http?.entryPath ?? '';
    this.scopeProviders = new ProviderRegistry(this.defaultScopeProviders, globalProviders);
    this.ready = this.initialize();
  }

  protected async initialize(): Promise<void> {
    await this.scopeProviders.ready;

    this.scopeHooks = new HookRegistry(this.scopeProviders, []);
    await this.scopeHooks.ready;

    this.scopeFlows = new FlowRegistry(this.scopeProviders, [HttpRequestFlow]);
    await this.scopeFlows.ready;

    this.transportService = new TransportService(this);

    this.scopeAuth = new AuthRegistry(this.scopeProviders, [], {
      kind: 'scope',
      id: this.id,
      ref: ScopeEntry,
    }, this.metadata.auth);
    await this.scopeAuth.ready

    this.scopeApps = new AppRegistry(this.scopeProviders, this.metadata.apps);
    await this.scopeApps.ready;

    this.scopeTools = new ToolRegistry(this.scopeProviders, [], {kind: 'scope', id: this.id, ref: Scope});
    await this.scopeTools.ready;


    await this.auth.ready;
    this.logger.info('Initializing multi-app scope', this.metadata);
    if (!this.metadata.auth) {
      // log large warning about using FrontMcp without authentication
      this.logger.warn(`\n\n*******************************\n  WARNING: FrontMcp is running without authentication. \n  This is a security risk and should only be used in development environments. \n*******************************\n\n`);
    }
  }

  private get defaultScopeProviders() {
    return [{
      scope: ProviderScope.GLOBAL,
      name: 'ScopeEntry',
      provide: ScopeEntry,
      useValue: this,
    }, {
      scope: ProviderScope.GLOBAL,
      name: 'Scope',
      provide: Scope,
      useValue: this,
    }, {
      scope: ProviderScope.GLOBAL,
      name: 'FrontMcpLogger',
      provide: FrontMcpLogger,
      useValue: this.logger,
    }, {
      scope: ProviderScope.SESSION,
      name: 'SessionProvider',
      provide: SessionProvider,
      useClass: SessionProvider,
    }];
  }

  get auth(): FrontMcpAuth {
    return this.scopeAuth.getPrimary();
  }

  get hooks(): HookRegistryInterface {
    return this.scopeHooks;
  }

  get authProviders(): AuthRegistry {
    return this.scopeAuth;
  }

  get providers() {
    return this.scopeProviders;
  }

  get apps(): AppRegistry {
    return this.scopeApps;
  }

  get tools(): ToolRegistry {
    return this.scopeTools;
  }


  registryFlows(...flows: FlowType[]) {
    return this.scopeFlows.registryFlows(flows);
  }

  runFlow<Name extends FlowName>(name: Name, input: FlowInputOf<Name>, deps?: Map<Token, Type>): Promise<FlowOutputOf<Name> | undefined> {
    return this.scopeFlows.runFlow(name, input, deps);
  }

  async runFlowForOutput<Name extends FlowName>(name: Name, input: FlowInputOf<Name>, deps?: Map<Token, Type>): Promise<FlowOutputOf<Name>> {
    const result = await this.scopeFlows.runFlow(name, input, deps);
    if (result) {
      return result;
    }
    throw new Error(`flow exist without output`);
  }
}
